import React, { useState } from 'react';
import { Check, ArrowRight, ArrowLeft, Building2, Ruler, Search, Sparkles } from 'lucide-react';
import { sendMessage } from '../data/messages';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INTERESTS = [
  { id: 'Structural Engineering', icon: Building2 },
  { id: 'Infrastructure Engineering', icon: Ruler },
  { id: 'Engineering Research', icon: Search },
  { id: 'Something else', icon: Sparkles },
];

/*
 * §7.1.4 — premium multi-step contact micro-form:
 *   1) pick what you need → 2) your details → 3) your message → animated success.
 * The chosen interest is embedded in the message text (Firestore rules only allow
 * name/email/message), and submission writes to the `messages` collection.
 */
export default function ContactForm() {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({ interest: '', name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // 'sending' | 'sent' | 'error'

  const set = (k, v) => {
    setValues((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const pickInterest = (id) => {
    set('interest', id);
    setStep(1);
  };

  const validateDetails = () => {
    const e = {};
    if (!values.name.trim()) e.name = 'Please enter your name.';
    if (!EMAIL_RE.test(values.email.trim())) e.email = 'Enter a valid email address.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateDetails()) setStep(2);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (values.message.trim().length < 10) {
      setErrors({ message: 'Message should be at least 10 characters.' });
      return;
    }
    setStatus('sending');
    try {
      const message = values.interest
        ? `Interested in: ${values.interest}\n\n${values.message}`
        : values.message;
      await sendMessage({ name: values.name, email: values.email, message });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const reset = () => {
    setValues({ interest: '', name: '', email: '', message: '' });
    setErrors({});
    setStatus(null);
    setStep(0);
  };

  if (status === 'sent') {
    return (
      <div className="cform cform--done" role="status" aria-live="polite">
        <div className="cform__check">
          <Check size={34} strokeWidth={3} />
        </div>
        <h3>Message sent</h3>
        <p>Thanks{values.name ? `, ${values.name.split(' ')[0]}` : ''} — we'll be in touch shortly.</p>
        <button className="btn btn--ghost" onClick={reset}>
          Send another
        </button>
      </div>
    );
  }

  return (
    <form className="cform" onSubmit={submit} noValidate>
      <div className="cform__progress" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`cform__step${i <= step ? ' is-active' : ''}`} />
        ))}
      </div>
      <p className="cform__count">Step {step + 1} of 3</p>

      {step === 0 && (
        <div className="cform__panel" key="s0">
          <h3 className="cform__q">What can we help you with?</h3>
          <div className="cform__chips">
            {INTERESTS.map(({ id, icon: Icon }) => (
              <button
                type="button"
                key={id}
                className={`cform__chip${values.interest === id ? ' is-selected' : ''}`}
                onClick={() => pickInterest(id)}
              >
                <Icon size={20} />
                <span>{id}</span>
              </button>
            ))}
          </div>

          {/* fills the space below the chips: a flowing engineering signal */}
          <div className="cform__art" aria-hidden="true">
            <svg className="cform__wave" viewBox="0 0 360 70" preserveAspectRatio="none">
              <path
                className="cform__wave-path cform__wave-path--back"
                d="M0 38 Q30 14 60 38 T120 38 T180 38 T240 38 T300 38 T360 38 T420 38 T480 38"
              />
              <path
                className="cform__wave-path"
                d="M0 38 Q30 62 60 38 T120 38 T180 38 T240 38 T300 38 T360 38 T420 38 T480 38"
              />
            </svg>
            <span className="cform__art-note">
              <span className="cform__art-dot" /> Avg. reply in under 24h
            </span>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="cform__panel" key="s1">
          <h3 className="cform__q">Tell us who you are</h3>
          <div className="field">
            <label htmlFor="cf-name">Name</label>
            <input
              id="cf-name"
              type="text"
              placeholder="Your name"
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              aria-invalid={!!errors.name}
              autoFocus
            />
            {errors.name && <span className="field__error">{errors.name}</span>}
          </div>
          <div className="field">
            <label htmlFor="cf-email">Email</label>
            <input
              id="cf-email"
              type="email"
              placeholder="you@email.com"
              value={values.email}
              onChange={(e) => set('email', e.target.value)}
              aria-invalid={!!errors.email}
            />
            {errors.email && <span className="field__error">{errors.email}</span>}
          </div>
          <div className="cform__nav">
            <button type="button" className="cform__back" onClick={back}>
              <ArrowLeft size={16} /> Back
            </button>
            <button type="button" className="btn btn--accent" onClick={next}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="cform__panel" key="s2">
          <h3 className="cform__q">What's your project about?</h3>
          <div className="field">
            <label htmlFor="cf-message">Message</label>
            <textarea
              id="cf-message"
              rows={4}
              placeholder="A few details about your project…"
              value={values.message}
              onChange={(e) => set('message', e.target.value)}
              aria-invalid={!!errors.message}
              autoFocus
            />
            {errors.message && <span className="field__error">{errors.message}</span>}
          </div>
          <div className="cform__nav">
            <button type="button" className="cform__back" onClick={back}>
              <ArrowLeft size={16} /> Back
            </button>
            <button type="submit" className="btn btn--accent" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send message'}
            </button>
          </div>
          {status === 'error' && <p className="field__error">Something went wrong. Please try again.</p>}
        </div>
      )}
    </form>
  );
}
